import type { SelectChangeEvent } from "@mui/material";
import { CircularProgress, FormControl, InputLabel, MenuItem } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import type { Option } from "../constants";
import { canvasSelectSx } from "../forms/styled/CanvasFormTextField.styled";
import { ComparativeOperatorsSelect as StyledSelect } from "./styled/comparativeoperatorsselect.styled";

export interface AssignmentOperatorsSelectProps {
	value: string;
	onChange: (value: string) => void;
	options?: Option[];
	/** When true, never loads from the API; use `options` (may be empty while parent loads metadata). */
	suppressAutoFetch?: boolean;
	fullWidth?: boolean;
	disabled?: boolean;
	label?: string;
}

const AssignmentOperatorsSelect = (props: AssignmentOperatorsSelectProps) => {
	const {
		value,
		onChange,
		options,
		suppressAutoFetch = false,
		fullWidth = true,
		disabled = false,
		label = "Operator",
	} = props;

	const authHeader = useAuthHeader();
	const [fetchedOperators, setFetchedOperators] = useState<Option[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (suppressAutoFetch) return;
		if (options?.length) return;

		let cancelled = false;
		const fetchOperators = async () => {
			setLoading(true);
			try {
				const response = await axios.get(apiRoutes.getAssignmentOperators, { headers: authHeader });
				if (!cancelled) {
					const mapped: Option[] = (response.data ?? []).map((op: { id: string; operator: string; label?: string }) => ({
						label: op.label ?? op.operator,
						value: op.operator,
					}));
					setFetchedOperators(mapped);
				}
			} catch {
				if (!cancelled) setFetchedOperators([]);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		fetchOperators();

		return () => { cancelled = true; };
	}, [authHeader, options, suppressAutoFetch]);

	const operators = suppressAutoFetch ? (options ?? []) : (options?.length ? options : fetchedOperators);

	return (
		<FormControl
			fullWidth={fullWidth}
			disabled={disabled}
			sx={{
				...canvasSelectSx,
				minWidth: 0,
				...(fullWidth
					? { flex: "0 0 auto", width: "auto", maxWidth: 140 }
					: {}),
				"& .MuiOutlinedInput-root": {
					minWidth: 0,
					overflow: "hidden",
				},
			}}
		>
			<InputLabel>{label}</InputLabel>
			<StyledSelect
				value={value}
				label={label}
				onChange={(event: SelectChangeEvent) => onChange(event.target.value as string)}
				endAdornment={loading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : undefined}
			>
				{operators.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{option.label}
					</MenuItem>
				))}
			</StyledSelect>
		</FormControl>
	);
};

export default AssignmentOperatorsSelect;
